import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | paid | failed | expired
  const [order, setOrder] = useState(null);
  const { clear } = useCart();
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let stop = false;
    const poll = async () => {
      if (stop) return;
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setOrder(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          clear();
          sessionStorage.removeItem("chopbox_pending_session");
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) {
          setStatus(data.payment_status);
          return;
        }
      } catch (err) {
        // Poll error — retry until attempts exhausted
        console.warn("Payment status poll failed, will retry:", err?.message || err);
      }
      if (attempts.current >= 20) {
        setStatus("failed");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [sessionId, clear]);

  return (
    <main data-testid="payment-success-page" className="max-w-3xl mx-auto px-6 md:px-10 py-20 md:py-28">
      <div className="chop-card chop-shadow p-8 md:p-14 text-center">
        {status === "polling" && (
          <>
            <Loader2 className="w-10 h-10 mx-auto text-[#1A6B2E] animate-spin" />
            <h1 className="font-display text-4xl md:text-5xl mt-6 tracking-tighter">Confirming your order…</h1>
            <p className="text-[#4E5C4F] mt-3">Hold tight, we're checking with Stripe.</p>
          </>
        )}

        {status === "paid" && (
          <>
            <CheckCircle2 className="w-14 h-14 mx-auto text-[#1A6B2E]" />
            <h1 data-testid="success-title" className="font-display text-4xl md:text-6xl mt-6 tracking-tighter">
              Chop's on its way!
            </h1>
            <p className="text-[#4E5C4F] mt-3">
              Order <span className="font-mono text-[#0E2B13]">{order?.order_id}</span> confirmed.
            </p>
            {order && (
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4 text-left">
                <Info label="Delivery date" value={order.delivery_date} />
                <Info label="Time slot" value={order.time_slot} />
                <Info label="Area" value={order.city} />
              </div>
            )}
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/menu" className="rounded-full border border-[#1A6B2E]/15 px-6 py-3 text-sm hover:bg-[#F4EFEB]">
                Order more
              </Link>
              <Link to="/orders" data-testid="view-orders-btn" className="btn-primary rounded-full px-6 py-3 text-sm font-semibold">
                View my orders
              </Link>
            </div>
          </>
        )}

        {(status === "failed" || status === "expired") && (
          <>
            <XCircle className="w-14 h-14 mx-auto text-[#1A6B2E]" />
            <h1 className="font-display text-4xl md:text-5xl mt-6 tracking-tighter">
              Payment didn't go through
            </h1>
            <p className="text-[#4E5C4F] mt-3">No worries — your cart's still safe. Try again?</p>
            <Link to="/checkout" className="btn-primary inline-block mt-8 rounded-full px-6 py-3 text-sm font-semibold">
              Back to checkout
            </Link>
          </>
        )}
      </div>
    </main>
  );
}

function Info({ label, value }) {
  return (
    <div className="rounded-2xl bg-[#F4EFEB]/60 p-4">
      <p className="font-label text-[10px] text-[#4E5C4F]">{label}</p>
      <p className="font-display text-base mt-1">{value}</p>
    </div>
  );
}
